/**
 * Priority subject for report sticky-banner [Subject] placeholders.
 * Pure helpers — safe on malformed / empty input.
 */

const SUBJECT_PLACEHOLDER = "[Subject]";

/**
 * @param {string} name
 * @returns {string}
 */
export function normalizeSubjectKey(name) {
  return String(name ?? "")
    .trim()
    .toLowerCase();
}

/**
 * Detects Math section names (aliases).
 * @param {string} displayName
 * @returns {boolean}
 */
export function isMathSubjectName(displayName) {
  const n = normalizeSubjectKey(displayName);
  if (!n) return false;
  if (n === "math" || n === "maths" || n === "mathematics" || n === "mathematic") return true;
  if (n.includes("mathematics") || n.includes("math ")) return true;
  return n.startsWith("math");
}

/**
 * @param {{ marksObtained?: unknown; totalMarks?: unknown }} section
 * @returns {number|null} Percent 0–100, or null if unusable.
 */
export function subjectSectionPercent(section) {
  const m = Number(section?.marksObtained);
  const t = Number(section?.totalMarks);
  if (!Number.isFinite(m) || !Number.isFinite(t) || t <= 0) return null;
  return Math.min(100, Math.max(0, (m / t) * 100));
}

/**
 * @param {Array<{ sectionName?: string; marksObtained?: unknown; totalMarks?: unknown }>} subjectSections
 * @returns {{ displayName: string } | null}
 */
export function resolvePrioritySubject(subjectSections) {
  if (!Array.isArray(subjectSections) || subjectSections.length === 0) {
    return null;
  }

  const rows = [];
  for (const s of subjectSections) {
    const displayName = String(s?.sectionName ?? "").trim();
    const pct = subjectSectionPercent(s);
    if (!displayName || pct == null) continue;
    rows.push({ displayName, pct });
  }

  if (!rows.length) {
    return null;
  }

  const mathRows = rows.filter((r) => isMathSubjectName(r.displayName));
  const pickLowestAmong = (list) => {
    if (!list.length) return null;
    let best = list[0];
    for (const r of list) {
      if (r.pct < best.pct) best = r;
    }
    return { displayName: best.displayName };
  };

  /** No Math → lowest scoring subject */
  if (!mathRows.length) {
    return pickLowestAmong(rows);
  }

  const math = mathRows[0];

  /** Math < 90 → always Math */
  if (math.pct < 90) {
    return { displayName: math.displayName };
  }

  const others = rows.filter((r) => !isMathSubjectName(r.displayName));

  if (!others.length) {
    return { displayName: math.displayName };
  }

  const anyOtherStrictlyHigher = others.some((o) => o.pct > math.pct);
  if (anyOtherStrictlyHigher) {
    return { displayName: math.displayName };
  }

  const allOthersStrictlyLower = others.every((o) => o.pct < math.pct);
  if (allOthersStrictlyLower) {
    return pickLowestAmong(rows);
  }

  /** Ties (not strictly higher / not all strictly lower) → Math */
  return { displayName: math.displayName };
}

/**
 * Replaces [Subject] in message part strings (centralized injection).
 * @param {Array<{ kind: string; content: string }>} messageParts
 * @param {string} subjectDisplayName
 * @returns {Array<{ kind: string; content: string }>}
 */
export function applySubjectPlaceholders(messageParts, subjectDisplayName) {
  if (!Array.isArray(messageParts) || !subjectDisplayName) {
    return messageParts?.map((p) => ({ ...p })) ?? [];
  }
  const safe = String(subjectDisplayName).trim() || "your subjects";
  return messageParts.map((p) => {
    if (typeof p?.content !== "string") return { ...p };
    return {
      ...p,
      content: p.content.split(SUBJECT_PLACEHOLDER).join(safe)
    };
  });
}

/**
 * @param {Array<{ kind: string; content: string }>} messageParts
 * @returns {boolean}
 */
export function messagePartsNeedSubject(messageParts) {
  if (!Array.isArray(messageParts)) return false;
  return messageParts.some((p) => typeof p?.content === "string" && p.content.includes(SUBJECT_PLACEHOLDER));
}
